import React from 'react';
import { 
  CheckCircle, 
  Users, 
  Target,
  Calendar,
  BarChart3,
  Monitor
} from 'lucide-react';

const Sep29Report = () => {
  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <Calendar className="w-8 h-8 text-blue-600" />
          AI Team Status Report - Sprint 11 Completion
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            <Calendar className="w-4 h-4" />
            September 29th, 2025
          </span>
          <span className="meta-item">
            <Target className="w-4 h-4" />
            Sprint 11 (Ending Sep 30th)
          </span>
        </div>
      </div>

      <div className="report-grid">
        {/* Sprint 11 Achievements */}
        <div className="report-section">
          <h2 className="section-title">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Sprint 11 Achievements
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Kinetic Phase 2 Deployment</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>Deployed Kinetic Phase 2 on staging environment</li>
                <li>Successfully deployed to production</li>
                <li>Production-ready deployment completed</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Asuretify Bug Resolution</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>Resolved 3 critical bugs in Sprint 11</li>
                <li>Reviewed and fixed all Asuretify bugs related to Prof-Poll/Monorepo</li>
                <li>Model fine-tuning/low priority bugs kept on hold</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Arize Integration & Monitoring</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>Implemented Arize as OpenTelemetry and tracing solution</li>
                <li>Deployed Arise.ai in RiskGuru and Asuretify platforms</li>
                <li>Enhanced monitoring and observability across platforms</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Project Feature Development</h3>
                <span className="status-badge in-progress">IN PROGRESS</span>
              </div>
              <ul className="achievement-list">
                <li>Started working on "Adding Project" feature in Kinetic</li>
                <li>Feature development in active progress</li>
                <li>Foundation work completed in Sprint 11</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sprint 11 KR Impact Summary */}
        <div className="report-section">
          <h2 className="section-title">
            <Target className="w-6 h-6 text-blue-600" />
            Sprint 11 KR Impact Summary
          </h2>
          
          <div className="kr-impact-summary">
            <p className="summary-intro">
              Sprint 11 achievements directly impacted <strong>6 Key Results (KRs)</strong> across <strong>3 Q3 OKR pillars</strong>, 
              demonstrating significant progress toward our quarterly objectives.
            </p>
            
            <div className="achievement-cards">
              <div className="achievement-card">
                <div className="achievement-header">
                  <h3>System Reliability</h3>
                  <span className="status-badge completed">2 KRs IMPACTED</span>
                </div>
                <ul className="achievement-list">
                  <li><strong>KR1:</strong> Achieve 99.5% uptime for all automated processing workflows</li>
                  <li><strong>Impact:</strong> Kinetic Phase 2 production deployment; Arize monitoring integration</li>
                  <li><strong>KR2:</strong> Establish production-ready operational processes</li>
                  <li><strong>Impact:</strong> Production-ready deployment; Arize observability</li>
                </ul>
              </div>

              <div className="achievement-card">
                <div className="achievement-header">
                  <h3>Information Accuracy</h3>
                  <span className="status-badge completed">1 KR IMPACTED</span>
                </div>
                <ul className="achievement-list">
                  <li><strong>KR3:</strong> Reduce data validation errors through automated quality checks</li>
                  <li><strong>Impact:</strong> Asuretify bug resolution (3 critical bugs)</li>
                </ul>
              </div>

              <div className="achievement-card">
                <div className="achievement-header">
                  <h3>AI Team Growth</h3>
                  <span className="status-badge completed">3 KRs IMPACTED</span>
                </div>
                <ul className="achievement-list">
                  <li><strong>KR1:</strong> Implement CI/CD pipeline with 90% test coverage and &lt;15 minute deployments</li>
                  <li><strong>Impact:</strong> Production deployment practices</li>
                  <li><strong>KR2:</strong> Platform upgrades to allow for scalable architecture</li>
                  <li><strong>Impact:</strong> Kinetic Phase 2 deployment; infrastructure improvements</li>
                  <li><strong>KR3:</strong> Code hygiene and standards</li>
                  <li><strong>Impact:</strong> Asuretify bug fixes and code quality improvements</li>
                </ul>
              </div>

              <div className="achievement-card">
                <div className="achievement-header">
                  <h3>Processing Speed & Scale</h3>
                  <span className="status-badge completed">2 KRs IMPACTED</span>
                </div>
                <ul className="achievement-list">
                  <li><strong>KR1:</strong> Reduce average processing time for 55-page document</li>
                  <li><strong>Impact:</strong> Kinetic Phase 2 performance improvements</li>
                  <li><strong>KR2:</strong> Handle processing spikes up to 2x normal volume</li>
                  <li><strong>Impact:</strong> Production deployment and monitoring infrastructure</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Baldwin Pilot Status */}
        <div className="report-section">
          <h2 className="section-title">
            <Users className="w-6 h-6 text-indigo-600" />
            Baldwin Pilot Status
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>IT Whitelisting</h3>
                <span className="status-badge in-progress">PENDING</span>
              </div>
              <ul className="achievement-list">
                <li>We are still waiting on their IT team to whitelist us</li>
                <li>Access approval process in progress</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Standards Meeting</h3>
                <span className="status-badge in-progress">SCHEDULED</span>
              </div>
              <ul className="achievement-list">
                <li>We have a meeting scheduled with them tomorrow on standards</li>
                <li>Preparing compliance and integration requirements</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Monitoring & Tracking</h3>
                <span className="status-badge completed">READY</span>
              </div>
              <ul className="achievement-list">
                <li>We have taken the opportunity to add tracking and tracing</li>
                <li>Ready to monitor and see usage data</li>
                <li>Enhanced observability infrastructure deployed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Arize Demo */}
        <div className="report-section">
          <h2 className="section-title">
            <Monitor className="w-6 h-6 text-cyan-600" />
            Arize Demo
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Live Monitoring Dashboard</h3>
                <span className="status-badge completed">ACTIVE</span>
              </div>
              <ul className="achievement-list">
                <li>Real-time observability and tracing across AI platforms</li>
                <li>Enhanced monitoring for RiskGuru and Asuretify</li>
                <li>
                  <a 
                    href="https://app.arize.com/organizations/QWNjb3VudE9yZ2FuaXphdGlvbjoyNzE0MDpDdFFr" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="demo-link"
                  >
                    View Live Arize Dashboard →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* PostHog Demo */}
        <div className="report-section">
          <h2 className="section-title">
            <BarChart3 className="w-6 h-6 text-emerald-600" />
            PostHog Demo
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Analytics & User Tracking</h3>
                <span className="status-badge completed">ACTIVE</span>
              </div>
              <ul className="achievement-list">
                <li>Comprehensive user behavior analytics and insights</li>
                <li>Event tracking and performance monitoring</li>
                <li>
                  <a 
                    href="https://us.posthog.com/project/222269" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="demo-link"
                  >
                    View PostHog Analytics Dashboard →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Sep29Report;
